import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  Alert,
  Chip,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack, Save, Storage } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { predictionService } from '../services/predictionService';
import { actualDataService, ActualDataSource, CashFlowData } from '../services/actualDataService';
import predictionComparisonService from '../services/predictionComparisonService';
import { Prediction } from '../types';
import { CashFlowTable } from '../components/prediction/CashFlowTable';
import { CashFlowChart } from '../components/prediction/CashFlowChart';
import { ActualDataSelector } from '../components/actualdata/ActualDataSelector';
import { Header } from '../components/layout/Header';
import { format } from 'date-fns';

export const PredictionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [prediction, setPrediction] = useState<Prediction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [purchasePrice, setPurchasePrice] = useState<number | ''>('');
  const [purchasePriceDisplay, setPurchasePriceDisplay] = useState<string>('');
  const [interestRate, setInterestRate] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Actual data comparison states
  const [actualDataSources, setActualDataSources] = useState<ActualDataSource[]>([]);
  const [selectedDataSourceId, setSelectedDataSourceId] = useState<number | ''>('');
  const [actualCashFlow, setActualCashFlow] = useState<CashFlowData | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Save comparison dialog states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [comparisonName, setComparisonName] = useState('');
  const [comparisonDescription, setComparisonDescription] = useState('');
  const [savingComparison, setSavingComparison] = useState(false);

  // Cache loading states
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheDialogOpen, setCacheDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadPrediction(Number(id));
      loadActualDataSources();
    }
  }, [id]);

  useEffect(() => {
    const comparisonId = searchParams.get('comparisonId');
    if (comparisonId && actualDataSources.length > 0) {
      loadComparison(Number(comparisonId));
    }
  }, [searchParams, actualDataSources]);

  // Check cache status when data source is selected
  useEffect(() => {
    if (selectedDataSourceId) {
      checkAndPopulateCache(selectedDataSourceId as number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataSourceId]);

  const loadComparison = async (comparisonId: number) => {
    try {
      const comparison = await predictionComparisonService.getComparison(comparisonId);

      // Set data source
      setSelectedDataSourceId(comparison.actualDataSourceId);

      // Parse credit type IDs from the stored string
      const creditTypeIds = comparison.creditTypeIds.split(',').map(id => parseInt(id.trim()));

      // Load the cash flow data
      const cashFlowData = await actualDataService.getCashFlow(
        comparison.actualDataSourceId,
        comparison.yearMonth,
        creditTypeIds
      );

      // Store selection params
      (cashFlowData as any).yearMonth = comparison.yearMonth;
      (cashFlowData as any).creditTypeIds = creditTypeIds;
      (cashFlowData as any).creditTypeNames = comparison.creditTypeNames;

      setActualCashFlow(cashFlowData);
      setSnackbar({
        open: true,
        message: `Összehasonlítás betöltve: ${comparison.name}`,
        severity: 'success'
      });
    } catch (err: any) {
      console.error('Failed to load comparison:', err);
      setSnackbar({
        open: true,
        message: 'Nem sikerült betölteni az összehasonlítást: ' + (err.response?.data?.error || err.message),
        severity: 'error'
      });
    }
  };

  const loadActualDataSources = async () => {
    try {
      const sources = await actualDataService.getDataSources();
      setActualDataSources(sources);
    } catch (err) {
      console.error('Failed to load actual data sources:', err);
    }
  };

  const loadPrediction = async (predictionId: number) => {
    try {
      setLoading(true);
      setError('');
      const data = await predictionService.getPrediction(predictionId);
      setPrediction(data);
      // Initialize parameters with original values
      const initialPrice = data.purchasePrice || '';
      setPurchasePrice(initialPrice);
      setPurchasePriceDisplay(initialPrice ? formatNumberWithSpaces(initialPrice) : '');
      setInterestRate(data.interestRate || '');
    } catch (err: any) {
      console.error('Failed to load prediction:', err);
      setError(err.response?.data?.error || 'Nem sikerült betölteni az előrejelzést');
    } finally {
      setLoading(false);
    }
  };

  const formatNumberWithSpaces = (num: number | string): string => {
    const str = num.toString().replace(/\s/g, '');
    return str.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const parseNumberFromSpaces = (str: string): number | '' => {
    const cleaned = str.replace(/\s/g, '');
    if (cleaned === '') return '';
    const num = Number(cleaned);
    return isNaN(num) ? '' : num;
  };

  const handlePurchasePriceChange = (value: string) => {
    // Allow only digits and spaces
    const filtered = value.replace(/[^\d\s]/g, '');
    setPurchasePriceDisplay(filtered);
    const numValue = parseNumberFromSpaces(filtered);
    setPurchasePrice(numValue);
    setHasChanges(true);
  };

  const handlePurchasePriceBlur = () => {
    // Reformat on blur
    if (purchasePrice !== '') {
      setPurchasePriceDisplay(formatNumberWithSpaces(purchasePrice));
    }
  };

  const handleInterestRateChange = (value: string) => {
    const numValue = value ? Number(value) : '';
    setInterestRate(numValue);
    setHasChanges(true);
  };

  const handleSaveParameters = async () => {
    if (!prediction) return;

    try {
      setSaving(true);
      setError('');

      const updates = {
        purchasePrice: purchasePrice !== '' ? Number(purchasePrice) : null,
        interestRate: interestRate !== '' ? Number(interestRate) : null,
      };

      const updatedPrediction = await predictionService.updatePrediction(prediction.id, updates);
      setPrediction(updatedPrediction);
      setHasChanges(false);

      // Show success message (optional)
      alert('Paraméterek sikeresen mentve!');

    } catch (err: any) {
      console.error('Failed to save parameters:', err);
      setError(err.response?.data?.error || 'Nem sikerült menteni a paramétereket');
    } finally {
      setSaving(false);
    }
  };

  const handleResetParameters = () => {
    const originalPrice = prediction?.purchasePrice || '';
    setPurchasePrice(originalPrice);
    setPurchasePriceDisplay(originalPrice ? formatNumberWithSpaces(originalPrice) : '');
    setInterestRate(prediction?.interestRate || '');
    setHasChanges(false);
  };

  const handleDataSourceChange = async (dataSourceId: number | '') => {
    setSelectedDataSourceId(dataSourceId);
    setActualCashFlow(null);
  };

  const handleActualDataSelection = async (yearMonth: string, creditTypeIds: number[], _totalCases: number, creditTypeNames: string) => {
    if (!selectedDataSourceId) return;

    try {
      const cashFlowData = await actualDataService.getCashFlow(
        selectedDataSourceId as number,
        yearMonth,
        creditTypeIds
      );

      // Store selection params for later saving
      (cashFlowData as any).yearMonth = yearMonth;
      (cashFlowData as any).creditTypeIds = creditTypeIds;
      (cashFlowData as any).creditTypeNames = creditTypeNames;

      setActualCashFlow(cashFlowData);
      setSnackbar({ open: true, message: `Tényadat betöltve: ${cashFlowData.totalCases} ügy`, severity: 'success' });
    } catch (err: any) {
      console.error('Failed to load actual cash flow:', err);
      setSnackbar({
        open: true,
        message: 'Nem sikerült betölteni a tényadatokat: ' + (err.response?.data?.error || err.message),
        severity: 'error'
      });
    }
  };

  const handleSaveComparison = async () => {
    if (!actualCashFlow || !selectedDataSourceId || !prediction) {
      setSnackbar({ open: true, message: 'Nincs aktív összehasonlítás mentéshez', severity: 'error' });
      return;
    }

    if (!comparisonName.trim()) {
      setSnackbar({ open: true, message: 'Adj meg nevet az összehasonlításnak!', severity: 'error' });
      return;
    }

    try {
      setSavingComparison(true);

      // Extract yearMonth and creditTypeIds from the actual data selection
      // These should be stored when actualCashFlow is loaded
      const yearMonth = (actualCashFlow as any).yearMonth || '';
      const creditTypeIds = (actualCashFlow as any).creditTypeIds || [];
      const creditTypeNames = (actualCashFlow as any).creditTypeNames || '';

      await predictionComparisonService.createComparison({
        predictionId: prediction.id,
        actualDataSourceId: selectedDataSourceId as number,
        name: comparisonName,
        description: comparisonDescription,
        yearMonth,
        creditTypeIds,
        creditTypeNames,
      });

      setSnackbar({ open: true, message: 'Összehasonlítás sikeresen mentve!', severity: 'success' });
      setSaveDialogOpen(false);
      setComparisonName('');
      setComparisonDescription('');
    } catch (err: any) {
      console.error('Failed to save comparison:', err);
      setSnackbar({
        open: true,
        message: 'Mentés sikertelen: ' + (err.response?.data?.error || err.message),
        severity: 'error'
      });
    } finally {
      setSavingComparison(false);
    }
  };

  const checkAndPopulateCache = async (id: number) => {
    setCacheLoading(true);
    setCacheDialogOpen(true);
    try {
      const { hasCache } = await actualDataService.hasCache(id);
      if (!hasCache) {
        await actualDataService.populateCache(id);
        setSnackbar({ open: true, message: 'Cache sikeresen betöltve!', severity: 'success' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Nem sikerült betölteni a cache-t', severity: 'error' });
    } finally {
      setCacheLoading(false);
      setCacheDialogOpen(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'RUNNING':
      case 'PENDING':
        return 'warning';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  if (error || !prediction) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Előrejelzés nem található'}
          </Alert>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>
            Vissza a Dashboard-ra
          </Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>
            Vissza
          </Button>
          <Typography variant="h4" component="h1">
            Előrejelzés Részletei
          </Typography>
        </Box>

        {/* Prediction Info - Compact */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Fájl név
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {prediction.uploadedFile?.fileName || `Prediction #${prediction.id}`}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Státusz
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={prediction.status}
                  color={getStatusColor(prediction.status) as any}
                  size="small"
                />
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Modell
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {prediction.modelName}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Létrehozva
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {format(new Date(prediction.createdAt), 'yyyy-MM-dd HH:mm')}
              </Typography>
            </Box>

            {prediction.completedAt && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Befejezve
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {format(new Date(prediction.completedAt), 'yyyy-MM-dd HH:mm')}
                  </Typography>
                </Box>
              </>
            )}

            {prediction.purchasePrice && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Vételár
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {prediction.purchasePrice.toLocaleString('hu-HU')} Ft
                  </Typography>
                </Box>
              </>
            )}

            {prediction.interestRate && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Kamatláb
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {prediction.interestRate}%
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {prediction.errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {prediction.errorMessage}
            </Alert>
          )}
        </Paper>

        {/* Results */}
        {prediction.status === 'COMPLETED' && prediction.results && (
          <Grid container spacing={3}>
            {/* Left Panel - Parameters */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Grafikon Paraméterek
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Módosítsa a paramétereket a grafikon újraszámolásához
                </Typography>

                <TextField
                  fullWidth
                  type="text"
                  label="Vételár (opcionális)"
                  value={purchasePriceDisplay}
                  onChange={(e) => handlePurchasePriceChange(e.target.value)}
                  onBlur={handlePurchasePriceBlur}
                  sx={{ mb: 2 }}
                  helperText="HUF"
                  placeholder="300 000 000"
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Tőke kamatköltség (%)"
                  value={interestRate}
                  onChange={(e) => handleInterestRateChange(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Éves kamatköltség százalékban"
                />

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleSaveParameters}
                  disabled={!hasChanges || saving}
                  sx={{ mb: 2 }}
                  startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                  {saving ? 'Mentés...' : 'Paraméterek Mentése'}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleResetParameters}
                  sx={{ mb: 2 }}
                  disabled={!hasChanges}
                >
                  Eredeti értékek visszaállítása
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Eredeti értékek:
                </Typography>
                {prediction.purchasePrice && (
                  <Typography variant="body2" color="text.secondary">
                    Vételár: {prediction.purchasePrice.toLocaleString('hu-HU')} Ft
                  </Typography>
                )}
                {prediction.interestRate && (
                  <Typography variant="body2" color="text.secondary">
                    Kamatláb: {prediction.interestRate}%
                  </Typography>
                )}
                {!prediction.purchasePrice && !prediction.interestRate && (
                  <Typography variant="body2" color="text.secondary">
                    Nincs megadva
                  </Typography>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Actual Data Comparison */}
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Storage /> Tényadat Összehasonlítás
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Válassz tényadat forrást az összehasonlításhoz
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Adatforrás</InputLabel>
                  <Select
                    value={selectedDataSourceId}
                    onChange={(e) => handleDataSourceChange(e.target.value as number | '')}
                    label="Adatforrás"
                  >
                    <MenuItem value="">
                      <em>Nincs kiválasztva</em>
                    </MenuItem>
                    {actualDataSources.map((source) => (
                      <MenuItem key={source.id} value={source.id}>
                        {source.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedDataSourceId && (
                  <Box sx={{ mt: 2 }}>
                    <ActualDataSelector
                      dataSourceId={selectedDataSourceId}
                      onSelectionChange={handleActualDataSelection}
                      disabled={cacheLoading}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Right Panel - Results */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Előrejelzés Eredmények
                  </Typography>
                  {actualCashFlow && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<Save />}
                      onClick={() => {
                        // Generate default name from yearMonth and creditTypeNames
                        const yearMonth = (actualCashFlow as any).yearMonth || '';
                        const creditTypeNames = (actualCashFlow as any).creditTypeNames || '';
                        const defaultName = yearMonth && creditTypeNames
                          ? `${yearMonth} ${creditTypeNames}`
                          : '';
                        setComparisonName(defaultName);
                        setSaveDialogOpen(true);
                      }}
                    >
                      Összehasonlítás mentése
                    </Button>
                  )}
                </Box>

                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
                  <Tab label="Grafikon" />
                  <Tab label="Táblázat" />
                </Tabs>

                {tabValue === 0 && (
                  <CashFlowChart
                    prediction={prediction}
                    interestRate={interestRate !== '' ? Number(interestRate) : undefined}
                    purchasePrice={purchasePrice !== '' ? Number(purchasePrice) : undefined}
                    actualCashFlow={actualCashFlow ? {
                      cashFlow: actualCashFlow.cashFlow,
                      totalCases: actualCashFlow.totalCases,
                      purchasePrice: actualCashFlow.purchasePrice,
                      initialDebt: actualCashFlow.initialDebt,
                      creditTypeNames: actualCashFlow.creditTypeNames,
                    } : null}
                    intervalDays={prediction.forecastIntervalDays || 30}
                    startDate={prediction.createdAt}
                  />
                )}

                {tabValue === 1 && (
                  <CashFlowTable
                    prediction={prediction}
                    purchasePrice={purchasePrice !== '' ? Number(purchasePrice) : undefined}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {prediction.status === 'RUNNING' || prediction.status === 'PENDING' ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Az előrejelzés futtatása folyamatban...</Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => loadPrediction(prediction.id)}
            >
              Frissítés
            </Button>
          </Paper>
        ) : null}

        {prediction.status === 'FAILED' && (
          <Paper sx={{ p: 3 }}>
            <Alert severity="error">
              Az előrejelzés sikertelen volt.
              {prediction.errorMessage && (
                <>
                  <br />
                  <strong>Hibaüzenet:</strong> {prediction.errorMessage}
                </>
              )}
            </Alert>
          </Paper>
        )}
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Save Comparison Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => !savingComparison && setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Összehasonlítás mentése</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Név"
              value={comparisonName}
              onChange={(e) => setComparisonName(e.target.value)}
              placeholder="pl. 2020-11 Folyószámlahitel"
              required
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Leírás (opcionális)"
              value={comparisonDescription}
              onChange={(e) => setComparisonDescription(e.target.value)}
              multiline
              rows={3}
              placeholder="További megjegyzések..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} disabled={savingComparison}>
            Mégse
          </Button>
          <Button
            onClick={handleSaveComparison}
            variant="contained"
            disabled={savingComparison || !comparisonName.trim()}
            startIcon={savingComparison ? <CircularProgress size={20} /> : <Save />}
          >
            {savingComparison ? 'Mentés...' : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cache Loading Dialog */}
      <Dialog
        open={cacheDialogOpen}
        maxWidth="xs"
        fullWidth
        onClose={() => {
          // Prevent closing if loading
          if (cacheLoading) return;
          setCacheDialogOpen(false);
        }}
        disableEscapeKeyDown={cacheLoading}
      >
        <DialogTitle>Cache betöltése</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Cache betöltése folyamatban... (Több percet is igénybe vehet.)</Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

